<script lang="ts">
	import Icon from '$lib/components/Icon/Icon.svelte';

	export let value: string | number = '';
	export let type: 'text' | 'email' | 'password' | 'number' | 'url' = 'text';
	export let label = '';
	export let helperText = '';
	export let required = false;
	export let disabled = false;
	export let invalid: string | null = null;
	export let autofocus = false;
	export let readonly = false;
	export let fullWidth = false;
	export let name = '';
	export let placeholder = '';
	export let hidden = false;

	let forceLTR = false;

	const isPasswordInput = type === 'password';
	const toggleShowPassword = () => {
		type = type === 'password' ? 'text' : 'password';
		forceLTR = true;
	};
</script>

<div class="wrapper" class:fullWidth class:hidden>
	<label>
		{#if label}
			{label}
		{/if}
		<!-- svelte-ignore a11y-autofocus -->
		<input
			class:invalid
			class:forceLTR
			{type}
			{disabled}
			{readonly}
			{hidden}
			{required}
			value={value ?? ''}
			{name}
			{placeholder}
			on:input
			on:input={({ target }) => {
				if (target instanceof HTMLInputElement) {
					value = type == 'number' ? Number(target.value) : target.value;
				}
			}}
			{autofocus}
			on:change
			on:keydown
			on:keyup
			on:focus
			on:blur
			on:paste
			on:click
			aria-required={required}
			aria-describedby="err-msg-{name}"
			aria-invalid={!!invalid} />
		{#if isPasswordInput}
			<button
				type="button"
				class="passwordIcon"
				on:click|stopPropagation|preventDefault={toggleShowPassword}>
				<Icon
					color="gray"
					clickable
					icon={type === 'password' ? 'hide-content' : 'view'}
					size="25" />
			</button>
		{/if}
	</label>

	{#if invalid}
		<div class="validationText">
			<Icon icon="warning" size="13" color="rgb(254 106 102)" />
			<div class="validationTextInner" id="err-msg-{name}">{invalid}</div>
		</div>
	{:else if helperText}
		<div class="helperText" aria-label={helperText || ''}>{helperText || ''}</div>
	{/if}
</div>

<style>
	label {
		display: block;
		position: relative;
	}

	label::first-letter {
		text-transform: capitalize;
	}

	.hidden {
		display: none;
	}

	.fullWidth {
		inline-size: 100%;
	}

	input {
		inline-size: 100%;
		padding: var(--gutterSmall);
		border: 1px solid rgb(204 204 204);
		border-radius: 4px;
		outline: none;
		font-size: var(--fontSizeSmall);
		line-height: 15px;
	}

	.passwordIcon {
		all: unset;
		position: absolute;
		inset-inline-end: 6px;
		inset-block-end: 5px;
		direction: ltr;
	}

	.wrapper {
		margin-block-end: var(--gutterSmaller);
		font-size: var(--fontSizeSmall);
		cursor: default;
	}

	.helperText,
	.validationText {
		margin-block-start: var(--gutterSmallest);
		margin-inline-start: var(--gutterSmallest);
		font-size: var(--fontSizeSmaller);
	}

	.validationText {
		display: flex;
		align-items: center;
		gap: var(--gutterSmaller);
	}

	.validationTextInner {
		color: var(--dangerColor);
		font-size: inherit;
	}

	.invalid {
		background-color: rgb(253 231 229);
	}

	input:focus {
		border: 1px solid var(--blueDark);
	}

	input:read-only {
		cursor: default;
	}

	.invalid:focus {
		border: 1px solid rgb(254 106 102);
	}

	input[type='email'],
	input[type='password'],
	.forceLTR {
		direction: ltr;
	}
</style>
